import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subject, interval } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { realTimeService } from 'src/app/services/services/realtime.service';
import { I18NextPipe } from 'angular-i18next';
import { translateStatus, statusKey, ContactCentreStatus } from 'src/app/shared/utils/status-utils';

export interface AgentStatusItem {
  firstname?: string | null;
  lastname?: string | null;
  formattedPhoneNumber?: string | null;
  statusIcon?: string | null;
  formattedStatus?: string | null; // raw from API; we’ll translate it in the template
  color?: string | null;
}

interface AgentsStatusDialogData {
  queueId: number;
  ajaxRefreshInterval: number;
}

@Component({
    selector: 'agents-status-dialog',
    templateUrl: './agents-status-dialog.component.html',
    styleUrls: ['./agents-status-dialog.component.scss'],
    standalone: false
})
export class AgentsStatusDialogComponent implements OnInit, OnDestroy {
  items: AgentStatusItem[] = [];
  isLoading = true;

  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<AgentsStatusDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AgentsStatusDialogData,
    private realTimeService: realTimeService,
    private i18n: I18NextPipe
  ) {}

  ngOnInit(): void {
    const intervalMs = Math.max(1000, Number(this.data.ajaxRefreshInterval) || 10000);
    this.fetch();

    interval(intervalMs)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.fetch(false));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

private fetch(showLoader = true): void {
  if (showLoader) this.isLoading = true;
  this.realTimeService.GetAgentStatusForQueue(this.data.queueId)
    .pipe(finalize(() => (this.isLoading = false)), takeUntil(this.destroy$))
    .subscribe(items => {
      this.items = (items ?? []).map((it: any) => {
        // Keep any "(...)" suffix from the server (counts / pause reason)
        const raw = String(it.FormattedStatus ?? it.formattedStatus ?? '');
        const suffix = (/\([^)]*\)\s*$/.exec(raw)?.[0]) ?? '';

        let label: string;

        // If CCS is missing (this endpoint usually doesn't include it),
        // treat the agent as logged on and map by UserStatusId only.
        if (it.UserStatusId != null) {
          const key = statusKey(
            (it.ContactCentreStatus ?? ContactCentreStatus.ReadyOrBusy), // <- prevent "Logged off" default
            it.UserStatusId
          );
          label = this.i18n.transform(key);
        } else if (raw) {
          // Fallback: translate from the server's text
          const base = raw.replace(/\s*\(.*\)\s*$/, '').trim().toLowerCase();
          const map: Record<string, string> = {
            'ready': 'status_available',
            'available': 'status_available',
            'busy': 'status_busy',
            'connected': 'status_connected',
            'connecting': 'status_connecting',
            'no answer': 'status_noAnswer',
            'wrap-up': 'status_wrapUp',
            'wrap up': 'status_wrapUp',
            'pause': 'status_pause',
            'logged off': 'status_loggedOff',
            'logged-off': 'status_loggedOff'
          };
          const key = map[base];
          label = key ? this.i18n.transform('realtime:' + key) : base;
        } else {
          label = '';
        }

        return {
          ...it,
          formattedStatus: `${label}${suffix}`
        };
      });
    });
}


  fullName(i: AgentStatusItem): string {
    const f = (i.firstname ?? '').trim();
    const l = (i.lastname ?? '').trim();
    return (f + ' ' + l).trim();
  }

  /**
   * Map backend status text to i18n keys from realtime.json
   * Falls back to original text if no mapping is found.
   */
  translateStatus(raw?: string | null): string {
  if (!raw) return '';

  // capture any trailing "(…)" so we can re-append after translation
  const match = raw.match(/\((.*?)\)\s*$/);
  const suffixRaw = match ? match[1].trim() : '';
  const hasSuffix = match && suffixRaw.length > 0;

  // base status text without the "(…)"
  const base = raw.replace(/\s*\(.*\)\s*$/, '').trim().toLowerCase();

  const map: Record<string, string> = {
    'ready': 'status_available',
    'available': 'status_available',
    'busy': 'status_busy',
    'connected': 'status_connected',
    'connecting': 'status_connecting',
    'no answer': 'status_noAnswer',
    'wrap-up': 'status_wrapUp',
    'wrap up': 'status_wrapUp',
    'pause': 'status_pause',
    'logged off': 'status_loggedOff',
    'logged-off': 'status_loggedOff'
  };

  const key = map[base];
  const label = key ? this.i18n.transform('realtime:' + key) : raw.replace(/\s*\(.*\)\s*$/, '').trim();

  // Keep numbers like "(0)" or "(1)"; hide empty "()"
  return hasSuffix ? `${label} (${suffixRaw})` : label;
}

  closeDialog(): void {
    this.dialogRef.close();
  }
}
